"use client";

import { ViewType } from "@supabase/auth-ui-shared";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import Button from "./Button";
import {
  anonymousIdForSignup,
  identifyNewSignup,
} from "../helpers/posthogIdentify";
import { SIGNUP_FLOW_EMAIL_CODE } from "../helpers/signupFlowExperiment";
import EmailCodeForm from "./EmailCodeForm";
import { useSupabaseClient } from "./SupabaseProvider";
import InfoTile from "./InfoTile";
import { TURNSTILE_FAILED_MESSAGE, useTurnstile } from "./TurnstileGate";

interface AuthUIProps {
  view?: ViewType;
  onViewChange?: (view: ViewType) => void;
  redirectTo?: string;
  magicLink?: boolean;
  // Overrides the sign-in / sign-up submit button text.
  submitLabels?: { sign_in?: string; sign_up?: string };
  // Replaces the default full-page Google redirect (the signup dialog signs
  // in through a popup instead). Called synchronously from the click, so the
  // handler can still open a window without tripping popup blockers.
  onOAuthSignIn?: (provider: "google") => void;
  // Confirm by a code typed in here rather than the email's link: tags new
  // users so the "Confirm signup" email carries one (signup-modal-flow
  // experiment, see helpers/signupFlowExperiment.ts), and asks for it once
  // the email is sent. Off, the pages keep today's link-only flow.
  emailCode?: boolean;
  // With emailCode, called instead of showing the built-in code entry once
  // a signup confirmation or magic link email is sent (the signup dialog
  // shows its own). `hasCode` is false when the email is known to carry only
  // a link: a signup for an address that already had an unconfirmed account,
  // whose stored metadata (and so its email) predates the code.
  onEmailSent?: (
    email: string,
    kind: "signup" | "magic_link",
    hasCode: boolean,
  ) => void;
}

const GoogleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 48 48"
    width="24px"
    height="24px"
    className="w-5 h-5 mr-2"
  >
    <path
      fill="#FFC107"
      d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.341,43.611,20.083z"
    />
    <path
      fill="#FF3D00"
      d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
    />
    <path
      fill="#1976D2"
      d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.341,43.611,20.083z"
    />
  </svg>
);

export default function AuthUI({
  view: propView,
  onViewChange,
  redirectTo,
  magicLink = true,
  emailCode = false,
  submitLabels,
  onOAuthSignIn,
  onEmailSent,
}: AuthUIProps) {
  const posthog = usePostHog();
  const supabase = useSupabaseClient();
  const turnstile = useTurnstile();
  const [localView, setLocalView] = useState<ViewType>(propView || "sign_in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // The confirmation / magic link email went out: enter its code here.
  const [codeStep, setCodeStep] = useState<{
    email: string;
    kind: "signup" | "magic_link";
  } | null>(null);
  const [status, setStatus] = useState<{
    type: "error" | "success";
    title: string;
    message: string;
  } | null>(null);

  // Sync propView to localView if it changes
  useEffect(() => {
    if (propView) {
      setLocalView(propView);
    }
  }, [propView]);

  const setView = (view: ViewType) => {
    setLocalView(view);
    setStatus(null);
    onViewChange?.(view);
  };

  const view = propView || localView;

  // Saved on a new account (Supabase ignores it for existing ones):
  // - signup_flow: read by the "Confirm signup" email template
  //   (`.Data.signup_flow`) to include the code.
  // - ph_anon_id: the visitor's anonymous PostHog id, merged into the user
  //   on their first sign-in in any browser (aliasSignupAnonymousId).
  const signupMetadata = () => {
    const anonymousId = anonymousIdForSignup(posthog);
    const data = {
      ...(emailCode && { signup_flow: SIGNUP_FLOW_EMAIL_CODE }),
      ...(anonymousId && { ph_anon_id: anonymousId }),
    };
    return Object.keys(data).length ? { data } : {};
  };

  const handleOAuthLogin = async (provider: "google") => {
    if (onOAuthSignIn) {
      setStatus(null);
      onOAuthSignIn(provider);
      return;
    }
    setLoading(true);
    setStatus(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
      },
    });
    if (error) {
      setStatus({
        type: "error",
        title: "Unable to sign in",
        message: error.message,
      });
      setLoading(false);
    }
    // Redirect happens automatically
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const captchaToken = await turnstile.getToken();
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });
      if (error) {
        setStatus({
          type: "error",
          title: "Unable to sign in",
          message: error.message,
        });
        setLoading(false);
      } else {
        // Success, redirection handled by parent or session change
      }
    } catch {
      // Only getToken() rejects here: Supabase returns auth failures as values.
      setStatus({
        type: "error",
        title: "Unable to sign in",
        message: TURNSTILE_FAILED_MESSAGE,
      });
      setLoading(false);
    } finally {
      // Tokens are single-use: leave a fresh challenge behind for the next try.
      turnstile.reset();
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const captchaToken = await turnstile.getToken();
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectTo,
          captchaToken,
          ...signupMetadata(),
        },
      });
      if (error) {
        setStatus({
          type: "error",
          title: "Unable to create account",
          message: error.message,
        });
        posthog.capture("Email Signup Error", { error: error.message });
      } else if (
        data.user &&
        data.user.identities &&
        data.user.identities.length === 0
      ) {
        setStatus({
          type: "error",
          title: "Account already exists",
          message: "This email is already registered. Please sign in instead.",
        });
      } else if (data.session) {
        // Signed in straight away (email confirmation off): the session
        // change takes it from here, there is no email to wait for.
      } else if (emailCode && onEmailSent) {
        onEmailSent(
          email,
          "signup",
          data.user?.user_metadata?.signup_flow === SIGNUP_FLOW_EMAIL_CODE,
        );
      } else if (
        emailCode &&
        data.user?.user_metadata?.signup_flow === SIGNUP_FLOW_EMAIL_CODE
      ) {
        setCodeStep({ email, kind: "signup" });
      } else {
        if (data.user) identifyNewSignup(posthog, data.user);
        setStatus({
          type: "success",
          title: "Confirmation email sent",
          message: "Please check your inbox to confirm your account.",
        });
      }
    } catch {
      setStatus({
        type: "error",
        title: "Unable to create account",
        message: TURNSTILE_FAILED_MESSAGE,
      });
      posthog.capture("Email Signup Error", { error: TURNSTILE_FAILED_MESSAGE });
    } finally {
      setLoading(false);
      turnstile.reset();
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const captchaToken = await turnstile.getToken();
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectTo,
          captchaToken,
          // Only kept for a brand-new user (who gets "Confirm signup").
          ...signupMetadata(),
        },
      });
      if (error) {
        setStatus({
          type: "error",
          title: "Unable to send magic link",
          message: error.message,
        });
      } else if (emailCode && onEmailSent) {
        // Can't tell from the response; the Magic Link template carries a
        // code for everyone, and "Confirm signup" does for new users.
        onEmailSent(email, "magic_link", true);
      } else if (emailCode) {
        setCodeStep({ email, kind: "magic_link" });
      } else {
        setStatus({
          type: "success",
          title: "Magic link sent",
          message: "We sent a magic link to your email address.",
        });
      }
    } catch {
      setStatus({
        type: "error",
        title: "Unable to send magic link",
        message: TURNSTILE_FAILED_MESSAGE,
      });
    } finally {
      setLoading(false);
      turnstile.reset();
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const captchaToken = await turnstile.getToken();
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        captchaToken,
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) {
        setStatus({
          type: "error",
          title: "Unable to send reset instructions",
          message: error.message,
        });
      } else {
        setStatus({
          type: "success",
          title: "Reset link sent",
          message: "We sent password reset instructions to your email.",
        });
      }
    } catch {
      setStatus({
        type: "error",
        title: "Unable to send reset instructions",
        message: TURNSTILE_FAILED_MESSAGE,
      });
    } finally {
      setLoading(false);
      turnstile.reset();
    }
  };

  const inputClasses =
    "text-base w-full px-4 py-2 text-slate-500 placeholder:text-sm placeholder-slate-500 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent font-sans";
  const labelClasses = "block mb-1 text-sm font-sans text-slate-500";
  const linkClasses =
    "text-sm text-slate-500 hover:underline cursor-pointer font-sans text-center mt-4 block";
  const buttonClasses =
    "!bg-slate-950 !rounded-xl !border-none !text-white text-sm font-medium font-display [&>*]:font-medium !font-display !font-sans w-full py-2";

  const renderStatus = () => {
    if (!status) return null;
    return (
      <div className="mt-4">
        <InfoTile title={status.title} description={status.message} />
      </div>
    );
  };

  if (codeStep) {
    // Signed in by the code, the session change takes over (AuthShell
    // redirects).
    return (
      <div className="w-full font-sans text-center">
        <p className="text-sm text-slate-500 mb-5">
          We sent an email to{" "}
          <span className="font-medium text-slate-700">{codeStep.email}</span>
          . Enter the code from it, or click its link.
        </p>
        <EmailCodeForm
          email={codeStep.email}
          kind={codeStep.kind}
          redirectTo={redirectTo}
        />
        {codeStep.kind === "signup" && (
          <button
            type="button"
            onClick={() => {
              setCodeStep(null);
              setView("sign_in");
            }}
            className={`${linkClasses} w-full`}
          >
            Already confirmed? Sign in
          </button>
        )}
        <button
          type="button"
          onClick={() => setCodeStep(null)}
          className={`${linkClasses} w-full`}
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <div className="w-full font-sans">
      <Button
        onClick={() => handleOAuthLogin("google")}
        className="w-full mb-4 !rounded-xl !border-none !text-white !font-sans !bg-slate-950 hover:!bg-slate-800"
        variant="secondary"
        loading={loading}
      >
        <GoogleIcon />
        {emailCode ? "Continue with Google" : "Sign in with Google"}
      </Button>

      <hr className="my-6 border-slate-200" />

      {view === "sign_in" && (
        <form onSubmit={handleEmailLogin} className="space-y-4">
          <div>
            <label className={labelClasses}>Email address</label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClasses}
              placeholder="Your email address"
              required
            />
          </div>
          <div>
            <label className={labelClasses}>Password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClasses}
              placeholder="Your password"
              required
            />
          </div>
          {turnstile.widget}
          <Button type="submit" loading={loading} className={buttonClasses}>
            {submitLabels?.sign_in ?? "Sign In"}
          </Button>
          {renderStatus()}
          <div className="text-center space-y-2 !mt-8">
            <a
              onClick={() => setView("forgotten_password")}
              className={linkClasses}
            >
              Forgot your password?
            </a>
            <a onClick={() => setView("sign_up")} className={linkClasses}>
              Don&apos;t have an account? Sign up
            </a>
            {magicLink && (
              <a onClick={() => setView("magic_link")} className={linkClasses}>
                Sign in with magic link
              </a>
            )}
          </div>
        </form>
      )}

      {view === "sign_up" && (
        <form onSubmit={handleSignup} className="space-y-4">
          <div>
            <label className={labelClasses}>Email address</label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClasses}
              placeholder="Your email address"
              required
            />
          </div>
          <div>
            <label className={labelClasses}>Password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClasses}
              placeholder="Your password"
              required
            />
          </div>
          {turnstile.widget}
          <Button type="submit" loading={loading} className={buttonClasses}>
            {submitLabels?.sign_up ?? "Sign up"}
          </Button>
          {renderStatus()}
          <div className="text-center space-y-2 mt-4">
            <a onClick={() => setView("sign_in")} className={linkClasses}>
              Already have an account? Sign in
            </a>
          </div>
        </form>
      )}

      {view === "magic_link" && (
        <form onSubmit={handleMagicLink} className="space-y-4">
          <div>
            <label className={labelClasses}>Email address</label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClasses}
              placeholder="Your email address"
              required
            />
          </div>
          {turnstile.widget}
          <Button type="submit" loading={loading} className={buttonClasses}>
            Send magic link
          </Button>
          {renderStatus()}
          <div className="text-center space-y-2 mt-4">
            <a onClick={() => setView("sign_in")} className={linkClasses}>
              Sign in with password
            </a>
          </div>
        </form>
      )}

      {view === "forgotten_password" && (
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div>
            <label className={labelClasses}>Email address</label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClasses}
              placeholder="Your email address"
              required
            />
          </div>
          {turnstile.widget}
          <Button type="submit" loading={loading} className={buttonClasses}>
            Send reset instructions
          </Button>
          {renderStatus()}
          <div className="text-center space-y-2 mt-4">
            <a onClick={() => setView("sign_in")} className={linkClasses}>
              Back to sign in
            </a>
          </div>
        </form>
      )}

      {view === "update_password" && (
        <div className="text-center">
          <p className="mb-4">Please use the update password page.</p>
          <a onClick={() => setView("sign_in")} className={linkClasses}>
            Back to sign in
          </a>
        </div>
      )}
    </div>
  );
}
